import React, { useCallback, useEffect, useState } from 'react';
import { FaCloudDownloadAlt, FaMapMarkerAlt, FaSpinner, FaStore, FaSyncAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import AdminHeader from '../../components/admin/AdminHeader';
import { anpService } from '../../services/anpService';
import { DataImportRun, ImportType } from '../../types';
import { formatDateTime } from '../../utils/formatters';

const TYPE_LABELS: Record<ImportType, string> = {
  ANP_PRECOS: 'Preços',
  ANP_CADASTRO: 'Cadastro de postos',
  GEOCODIFICACAO: 'Geocodificação',
  CONTATO_RECEITA: 'Contatos (Receita)',
};

type Action = 'precos' | 'cadastro' | 'geocode' | 'toggle';

const AdminAnp: React.FC = () => {
  const [ativo, setAtivo] = useState<boolean | null>(null);
  const [runs, setRuns] = useState<DataImportRun[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busy, setBusy] = useState<Action | null>(null);

  const load = useCallback(async () => {
    try {
      const [precosAtivo, recent] = await Promise.all([anpService.getPrecosAtivo(), anpService.recentRuns()]);
      setAtivo(precosAtivo);
      setRuns(recent);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar dados da ANP');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async () => {
    if (ativo === null) return;
    const novo = !ativo;
    if (!novo && !window.confirm('Desativar os preços da ANP? Eles somem das oportunidades na hora e a importação automática para.')) {
      return;
    }
    setBusy('toggle');
    try {
      setAtivo(await anpService.setPrecosAtivo(novo));
      toast.success(novo ? 'Preços da ANP ativados' : 'Preços da ANP desativados');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao alterar configuração');
    } finally {
      setBusy(null);
    }
  };

  const run = async (action: Exclude<Action, 'toggle'>) => {
    setBusy(action);
    try {
      const result =
        action === 'precos'
          ? await anpService.importPrecos()
          : action === 'cadastro'
            ? await anpService.importCadastro()
            : await anpService.geocode();
      if (result.status === 'ERRO') {
        toast.error(`Importação falhou: ${result.mensagem || 'erro desconhecido'}`);
      } else {
        toast.success('Importação concluída');
      }
      load();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao importar');
    } finally {
      setBusy(null);
    }
  };

  const actionButton = (action: Exclude<Action, 'toggle'>, label: string, Icon: React.ElementType, disabled = false) => (
    <button
      onClick={() => run(action)}
      disabled={busy !== null || disabled}
      className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 text-white/90 hover:bg-white/5 disabled:opacity-50"
    >
      {busy === action ? <FaSpinner className="animate-spin" /> : <Icon />} {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#0f1419] pb-24 lg:pb-0">
      <AdminHeader />

      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Dados da ANP</h1>
        <p className="text-white/60 text-sm mt-1 mb-6">
          Cadastro de postos e levantamento semanal de preços publicados pela ANP.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-20 text-[#089453]">
            <FaSpinner className="animate-spin" size={32} />
          </div>
        ) : (
          <>
            <section className="p-5 rounded-xl bg-[#1a2332] border border-white/10 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="max-w-2xl space-y-1">
                  <p className="text-white font-semibold">Preços da ANP nas oportunidades</p>
                  <p className="text-sm text-white/60">
                    Mostra nas oportunidades o preço da última pesquisa da ANP. O app não mostra preços nem postos
                    da ANP. A ANP pesquisa só parte dos postos, com até uma semana de atraso; preços com mais de 35
                    dias não aparecem.
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={!!ativo}
                  aria-label="Preços da ANP nas oportunidades"
                  onClick={toggle}
                  disabled={busy !== null || ativo === null}
                  className="flex items-center gap-3 disabled:opacity-60"
                >
                  <span className={`text-sm font-medium ${ativo ? 'text-[#84cc34]' : 'text-white/60'}`}>
                    {ativo ? 'Ativado' : 'Desativado'}
                  </span>
                  <span
                    className={`relative inline-flex h-7 w-12 rounded-full transition-colors ${
                      ativo ? 'bg-[#089453]' : 'bg-white/20'
                    }`}
                  >
                    <span
                      className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${ativo ? 'left-6' : 'left-1'}`}
                    />
                  </span>
                </button>
              </div>
            </section>

            <section className="flex flex-wrap gap-2 mb-6">
              {actionButton('precos', 'Importar preços agora', FaCloudDownloadAlt, !ativo)}
              {actionButton('cadastro', 'Importar cadastro de postos', FaStore)}
              {actionButton('geocode', 'Geocodificar pendentes', FaMapMarkerAlt)}
              <button
                onClick={load}
                disabled={busy !== null}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white/60 hover:text-white disabled:opacity-50"
              >
                <FaSyncAlt /> Atualizar
              </button>
            </section>

            <h2 className="text-lg font-semibold text-white mb-3">Últimas execuções</h2>
            {runs.length === 0 ? (
              <div className="text-center py-12 rounded-2xl bg-[#1a2332] border border-white/10 text-white/60">
                Nenhuma importação ainda.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm text-left">
                  <thead className="bg-white/5 text-white/60">
                    <tr>
                      <th className="px-4 py-3 font-medium">Tipo</th>
                      <th className="px-4 py-3 font-medium">Início</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium text-right">Lidas</th>
                      <th className="px-4 py-3 font-medium text-right">Postos criados</th>
                      <th className="px-4 py-3 font-medium text-right">Postos atualizados</th>
                      <th className="px-4 py-3 font-medium text-right">Preços</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5 text-white/85">
                    {runs.map((r) => (
                      <tr key={r.id} className="bg-[#1a2332]">
                        <td className="px-4 py-3">{TYPE_LABELS[r.tipo] || r.tipo}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(r.iniciadoEm)}</td>
                        <td className="px-4 py-3">
                          <span
                            className={
                              r.status === 'SUCESSO'
                                ? 'text-[#84cc34]'
                                : r.status === 'ERRO'
                                  ? 'text-red-300'
                                  : 'text-yellow-300'
                            }
                            title={r.mensagem}
                          >
                            {r.status === 'SUCESSO' ? 'Sucesso' : r.status === 'ERRO' ? 'Erro' : 'Em andamento'}
                          </span>
                          {r.mensagem && <p className="text-xs text-white/50 max-w-xs truncate">{r.mensagem}</p>}
                        </td>
                        <td className="px-4 py-3 text-right">{r.linhasLidas}</td>
                        <td className="px-4 py-3 text-right">{r.postosCriados}</td>
                        <td className="px-4 py-3 text-right">{r.postosAtualizados}</td>
                        <td className="px-4 py-3 text-right">{r.precosAtualizados}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminAnp;
